import {
  FinancialPostingRuleDirection,
  FinancialPostingRuleRecord,
  FinancialPostingRuleStatus,
  JournalSourceModule,
} from '../../../../types/accounting';

export const CUSTOM_EVENT_TYPE_VALUE = '__custom__';
export const CUSTOM_REFERENCE_TYPE_VALUE = '__custom__';

export interface PostingRuleMetadataFieldDefinition {
  key: string;
  label: string;
  type: 'text' | 'select';
  placeholder?: string;
  description?: string;
  options?: { value: string; label: string }[];
}

export interface PostingRuleEventDefinition {
  value: string;
  label: string;
  sourceModule: JournalSourceModule;
  description: string;
  allowedReferenceTypes?: string[];
  defaultReferenceType?: string;
  metadataFields?: PostingRuleMetadataFieldDefinition[];
  defaultMetadata?: {
    category?: string;
    scenario?: string;
    channel?: string;
  };
}

export interface PostingRuleReferenceDefinition {
  value: string;
  label: string;
  sourceModule: JournalSourceModule;
  description: string;
}

export interface PostingRuleFormState {
  ruleName: string;
  sourceModule: JournalSourceModule;
  eventType: string;
  customEventType: string;
  referenceType: string;
  customReferenceType: string;
  direction: FinancialPostingRuleDirection;
  accountId: string;
  taxRateId: string;
  paymentMethodId: string;
  priority: string;
  status: FinancialPostingRuleStatus;
  metadataCategory: string;
  metadataScenario: string;
  metadataChannel: string;
  eventMetadataValues: Record<string, string>;
  metadataJson: string;
}

export const JOURNAL_SOURCE_MODULE_OPTIONS: {
  value: JournalSourceModule;
  label: string;
}[] = [
  { value: 'sales', label: 'Sales' },
  { value: 'inventory', label: 'Inventory' },
  { value: 'accommodation', label: 'Accommodation' },
  { value: 'booking_com', label: 'Booking.com' },
  { value: 'manual', label: 'Manual' },
  { value: 'system', label: 'System' },
];

export const POSTING_RULE_DIRECTION_OPTIONS: {
  value: FinancialPostingRuleDirection;
  label: string;
}[] = [
  { value: 'debit', label: 'Debit' },
  { value: 'credit', label: 'Credit' },
];

export const POSTING_RULE_STATUS_OPTIONS: {
  value: FinancialPostingRuleStatus;
  label: string;
}[] = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

export const POSTING_RULE_EVENT_DEFINITIONS: PostingRuleEventDefinition[] = [
  {
    value: 'sale_completed',
    label: 'Sale completed',
    sourceModule: 'sales',
    description:
      'Recognize a completed outlet sale into revenue, tax, and settlement accounts.',
    allowedReferenceTypes: ['order', 'order_settlement'],
    defaultReferenceType: 'order',
    metadataFields: [
      {
        key: 'sale_stage',
        label: 'Sale stage',
        type: 'select',
        options: [
          { value: 'finalized', label: 'Finalized' },
          {
            value: 'served_pending_settlement',
            label: 'Served pending settlement',
          },
        ],
      },
    ],
    defaultMetadata: { category: 'sale', channel: 'pos' },
  },
  {
    value: 'sales_order_refund',
    label: 'Sales order refund',
    sourceModule: 'sales',
    description:
      'Reverse revenue, tax, and settlement impact for a refunded sale.',
    allowedReferenceTypes: ['order', 'order_settlement'],
    defaultReferenceType: 'order_settlement',
    metadataFields: [
      {
        key: 'refund_scope',
        label: 'Refund scope',
        type: 'select',
        options: [
          { value: 'full', label: 'Full refund' },
          { value: 'partial', label: 'Partial refund' },
        ],
      },
    ],
    defaultMetadata: {
      category: 'refund',
      scenario: 'sales-order-refund',
      channel: 'pos',
    },
  },
  {
    value: 'sale_credit_issued',
    label: 'Credit sale issued',
    sourceModule: 'sales',
    description:
      'Record sales that move to receivables instead of immediate settlement.',
    allowedReferenceTypes: ['order', 'order_settlement'],
    defaultReferenceType: 'order',
    metadataFields: [
      {
        key: 'receivable_type',
        label: 'Receivable type',
        type: 'select',
        options: [
          { value: 'guest_credit', label: 'Guest credit' },
          { value: 'corporate_credit', label: 'Corporate credit' },
        ],
      },
    ],
    defaultMetadata: { category: 'credit', channel: 'pos' },
  },
  {
    value: 'sale_non_chargeable',
    label: 'Non-chargeable sale',
    sourceModule: 'sales',
    description:
      'Record NC consumption with the right discount and expense treatment.',
    allowedReferenceTypes: ['order'],
    defaultReferenceType: 'order',
    metadataFields: [
      {
        key: 'nc_reason',
        label: 'NC reason',
        type: 'select',
        options: [
          { value: 'staff_meal', label: 'Staff meal' },
          { value: 'owner_comp', label: 'Owner comp' },
          { value: 'promotion', label: 'Promotion' },
        ],
      },
    ],
    defaultMetadata: { category: 'non_chargeable', channel: 'pos' },
  },
  {
    value: 'purchase_received',
    label: 'Purchase received',
    sourceModule: 'inventory',
    description:
      'Record stock receipt, supplier liability, and purchase-side tax effects.',
    allowedReferenceTypes: ['supplier_purchase'],
    defaultReferenceType: 'supplier_purchase',
    metadataFields: [
      {
        key: 'purchase_stage',
        label: 'Purchase stage',
        type: 'select',
        options: [
          { value: 'received', label: 'Goods received' },
          { value: 'invoiced', label: 'Invoice recognized' },
        ],
      },
    ],
    defaultMetadata: { category: 'purchase', channel: 'inventory' },
  },
  {
    value: 'inventory_adjustment',
    label: 'Inventory adjustment',
    sourceModule: 'inventory',
    description:
      'Post inventory write-offs, gains, or manual stock corrections.',
    allowedReferenceTypes: ['stock_adjustment'],
    defaultReferenceType: 'stock_adjustment',
    metadataFields: [
      {
        key: 'adjustment_reason',
        label: 'Adjustment reason',
        type: 'select',
        options: [
          { value: 'write_off', label: 'Write-off' },
          { value: 'variance', label: 'Variance' },
          { value: 'stock_count', label: 'Stock count' },
        ],
      },
    ],
    defaultMetadata: { category: 'adjustment', channel: 'inventory' },
  },
  {
    value: 'cogs_recognized',
    label: 'COGS recognized',
    sourceModule: 'inventory',
    description:
      'Move inventory value into cost of goods sold after consumption.',
    allowedReferenceTypes: ['order', 'stock_adjustment'],
    defaultReferenceType: 'order',
    metadataFields: [
      {
        key: 'cost_basis',
        label: 'Cost basis',
        type: 'select',
        options: [
          { value: 'recipe_consumption', label: 'Recipe consumption' },
          { value: 'manual_adjustment', label: 'Manual adjustment' },
        ],
      },
    ],
    defaultMetadata: { category: 'cogs', channel: 'inventory' },
  },
  {
    value: 'booking_deposit_received',
    label: 'Booking deposit received',
    sourceModule: 'accommodation',
    description: 'Record guest deposits into cash and unearned revenue.',
    allowedReferenceTypes: ['booking', 'booking_payment'],
    defaultReferenceType: 'booking_payment',
    metadataFields: [
      {
        key: 'deposit_kind',
        label: 'Deposit kind',
        type: 'select',
        options: [
          { value: 'advance_payment', label: 'Advance payment' },
          { value: 'booking_guarantee', label: 'Booking guarantee' },
        ],
      },
    ],
    defaultMetadata: { category: 'deposit', channel: 'accommodation' },
  },
  {
    value: 'night_audit_revenue_recognized',
    label: 'Night audit revenue recognized',
    sourceModule: 'accommodation',
    description:
      'Recognize earned room revenue during nightly revenue release.',
    allowedReferenceTypes: ['booking', 'night_audit'],
    defaultReferenceType: 'night_audit',
    metadataFields: [
      {
        key: 'recognition_basis',
        label: 'Recognition basis',
        type: 'select',
        options: [
          { value: 'per_night', label: 'Per night' },
          { value: 'checkout_release', label: 'Checkout release' },
        ],
      },
    ],
    defaultMetadata: {
      category: 'revenue_recognition',
      channel: 'accommodation',
    },
  },
  {
    value: 'accommodation_booking_refund',
    label: 'Accommodation booking refund',
    sourceModule: 'accommodation',
    description:
      'Refund paid bookings by unwinding settlement and unearned revenue positions.',
    allowedReferenceTypes: ['booking', 'booking_payment'],
    defaultReferenceType: 'booking_payment',
    metadataFields: [
      {
        key: 'refund_scope',
        label: 'Refund scope',
        type: 'select',
        options: [
          { value: 'full', label: 'Full refund' },
          { value: 'partial', label: 'Partial refund' },
        ],
      },
    ],
    defaultMetadata: {
      category: 'refund',
      scenario: 'accommodation-booking-refund',
      channel: 'accommodation',
    },
  },
  {
    value: 'accommodation_cancellation_fee_retained',
    label: 'Cancellation fee retained',
    sourceModule: 'accommodation',
    description:
      'Split a cancelled paid booking between retained fee revenue and refunded balance.',
    allowedReferenceTypes: ['booking', 'booking_payment'],
    defaultReferenceType: 'booking',
    metadataFields: [
      {
        key: 'fee_policy',
        label: 'Fee policy',
        type: 'select',
        options: [
          { value: 'retain_partial', label: 'Retain partial amount' },
          { value: 'retain_fixed_fee', label: 'Retain fixed fee' },
        ],
      },
    ],
    defaultMetadata: {
      category: 'refund',
      scenario: 'accommodation-cancellation-fee',
      channel: 'accommodation',
    },
  },
  {
    value: 'booking_com_commission_recorded',
    label: 'Booking.com commission recorded',
    sourceModule: 'booking_com',
    description:
      'Recognize OTA commission expense separately from gross booking revenue.',
    allowedReferenceTypes: ['booking_com_reservation', 'booking_com_payout'],
    defaultReferenceType: 'booking_com_reservation',
    metadataFields: [
      {
        key: 'commission_stage',
        label: 'Commission stage',
        type: 'select',
        options: [
          { value: 'reservation_confirmed', label: 'Reservation confirmed' },
          { value: 'payout_reconciled', label: 'Payout reconciled' },
        ],
      },
    ],
    defaultMetadata: { category: 'commission', channel: 'booking_com' },
  },
  {
    value: 'booking_com_payout_received',
    label: 'Booking.com payout received',
    sourceModule: 'booking_com',
    description:
      'Record OTA payout receipts against receivables and clearing balances.',
    allowedReferenceTypes: ['booking_com_payout'],
    defaultReferenceType: 'booking_com_payout',
    metadataFields: [
      {
        key: 'payout_mode',
        label: 'Payout mode',
        type: 'select',
        options: [
          { value: 'net_payout', label: 'Net payout' },
          { value: 'gross_reconciliation', label: 'Gross reconciliation' },
        ],
      },
    ],
    defaultMetadata: { category: 'payout', channel: 'booking_com' },
  },
  {
    value: 'booking_com_refund_adjustment',
    label: 'Booking.com refund adjustment',
    sourceModule: 'booking_com',
    description:
      'Handle OTA refund and payout corrections when Booking.com adjusts collectible balances.',
    allowedReferenceTypes: ['booking_com_reservation', 'booking_com_payout'],
    defaultReferenceType: 'booking_com_payout',
    metadataFields: [
      {
        key: 'adjustment_scope',
        label: 'Adjustment scope',
        type: 'select',
        options: [
          { value: 'refund', label: 'Refund' },
          { value: 'chargeback', label: 'Chargeback' },
          { value: 'payout_correction', label: 'Payout correction' },
        ],
      },
    ],
    defaultMetadata: {
      category: 'refund',
      scenario: 'booking-com-refund',
      channel: 'booking_com',
    },
  },
  {
    value: 'manual_journal_support',
    label: 'Manual journal support',
    sourceModule: 'manual',
    description:
      'Support helper mappings for manual accounting flows where automation still applies.',
    allowedReferenceTypes: ['manual_reference'],
    defaultReferenceType: 'manual_reference',
    metadataFields: [
      {
        key: 'manual_group',
        label: 'Manual group',
        type: 'text',
        placeholder: 'e.g. opening-balance-support',
      },
    ],
    defaultMetadata: { category: 'manual', channel: 'manual' },
  },
  {
    value: 'system_adjustment',
    label: 'System adjustment',
    sourceModule: 'system',
    description:
      'Reserve system-owned posting rules for opening balances, rollovers, or automated adjustments.',
    allowedReferenceTypes: ['system_job'],
    defaultReferenceType: 'system_job',
    metadataFields: [
      {
        key: 'system_process',
        label: 'System process',
        type: 'text',
        placeholder: 'e.g. year-end-rollover',
      },
    ],
    defaultMetadata: { category: 'system', channel: 'system' },
  },
];

export const POSTING_RULE_REFERENCE_DEFINITIONS: PostingRuleReferenceDefinition[] =
  [
    {
      value: 'order',
      label: 'Order',
      sourceModule: 'sales',
      description: 'Apply the rule against the order record itself.',
    },
    {
      value: 'order_settlement',
      label: 'Order settlement',
      sourceModule: 'sales',
      description: 'Apply the rule against payment or settlement records.',
    },
    {
      value: 'supplier_purchase',
      label: 'Supplier purchase',
      sourceModule: 'inventory',
      description: 'Apply the rule against supplier purchasing transactions.',
    },
    {
      value: 'stock_adjustment',
      label: 'Stock adjustment',
      sourceModule: 'inventory',
      description: 'Apply the rule against stock adjustment events.',
    },
    {
      value: 'booking',
      label: 'Booking',
      sourceModule: 'accommodation',
      description: 'Apply the rule against accommodation booking records.',
    },
    {
      value: 'booking_payment',
      label: 'Booking payment',
      sourceModule: 'accommodation',
      description: 'Apply the rule against booking payment records.',
    },
    {
      value: 'night_audit',
      label: 'Night audit',
      sourceModule: 'accommodation',
      description: 'Apply the rule against nightly revenue recognition runs.',
    },
    {
      value: 'booking_com_reservation',
      label: 'Booking.com reservation',
      sourceModule: 'booking_com',
      description: 'Apply the rule against imported OTA reservation records.',
    },
    {
      value: 'booking_com_payout',
      label: 'Booking.com payout',
      sourceModule: 'booking_com',
      description: 'Apply the rule against OTA payout records.',
    },
    {
      value: 'manual_reference',
      label: 'Manual reference',
      sourceModule: 'manual',
      description: 'Use a manual business reference for assisted workflows.',
    },
    {
      value: 'system_job',
      label: 'System job',
      sourceModule: 'system',
      description: 'Apply the rule against system-generated accounting jobs.',
    },
  ];

export const getPostingRuleEventOptions = (
  sourceModule: JournalSourceModule,
): PostingRuleEventDefinition[] =>
  POSTING_RULE_EVENT_DEFINITIONS.filter(
    (eventDefinition) => eventDefinition.sourceModule === sourceModule,
  );

export const getPostingRuleEventDefinition = (
  sourceModule: JournalSourceModule,
  eventType: string,
): PostingRuleEventDefinition | null =>
  POSTING_RULE_EVENT_DEFINITIONS.find(
    (eventDefinition) =>
      eventDefinition.sourceModule === sourceModule &&
      eventDefinition.value === eventType,
  ) || null;

export const getPostingRuleReferenceOptions = (
  sourceModule: JournalSourceModule,
  eventType?: string,
): PostingRuleReferenceDefinition[] =>
  POSTING_RULE_REFERENCE_DEFINITIONS.filter((referenceDefinition) => {
    if (referenceDefinition.sourceModule !== sourceModule) {
      return false;
    }

    if (!eventType || eventType === CUSTOM_EVENT_TYPE_VALUE) {
      return true;
    }

    const eventDefinition = getPostingRuleEventDefinition(
      sourceModule,
      eventType,
    );

    if (!eventDefinition?.allowedReferenceTypes?.length) {
      return true;
    }

    return eventDefinition.allowedReferenceTypes.includes(
      referenceDefinition.value,
    );
  });

export const getPostingRuleReferenceDefinition = (
  sourceModule: JournalSourceModule,
  referenceType: string,
): PostingRuleReferenceDefinition | null =>
  POSTING_RULE_REFERENCE_DEFINITIONS.find(
    (referenceDefinition) =>
      referenceDefinition.sourceModule === sourceModule &&
      referenceDefinition.value === referenceType,
  ) || null;

const parseKnownMetadata = (metadata: Record<string, unknown> | null) => {
  if (!metadata) {
    return {
      metadataCategory: '',
      metadataScenario: '',
      metadataChannel: '',
      eventMetadataValues: {},
      metadataJson: '',
    };
  }

  const { category, scenario, channel, ...customMetadata } = metadata;

  return {
    metadataCategory: typeof category === 'string' ? category : '',
    metadataScenario: typeof scenario === 'string' ? scenario : '',
    metadataChannel: typeof channel === 'string' ? channel : '',
    eventMetadataValues: {},
    metadataJson: Object.keys(customMetadata).length
      ? JSON.stringify(customMetadata, null, 2)
      : '',
  };
};

export const createEmptyPostingRuleForm = (): PostingRuleFormState => ({
  ruleName: '',
  sourceModule: 'sales',
  eventType: 'sale_completed',
  customEventType: '',
  referenceType: 'order',
  customReferenceType: '',
  direction: 'debit',
  accountId: '',
  taxRateId: '',
  paymentMethodId: '',
  priority: '100',
  status: 'active',
  metadataCategory: 'sale',
  metadataScenario: '',
  metadataChannel: 'pos',
  eventMetadataValues: {},
  metadataJson: '',
});

export const createPostingRuleFormFromRecord = (
  rule: FinancialPostingRuleRecord,
): PostingRuleFormState => {
  const eventDefinition = getPostingRuleEventDefinition(
    rule.source_module,
    rule.event_type,
  );
  const knownMetadata = parseKnownMetadata(rule.metadata);
  const eventMetadataValues: Record<string, string> = {};

  if (eventDefinition?.metadataFields?.length && rule.metadata) {
    eventDefinition.metadataFields.forEach((field) => {
      const fieldValue = rule.metadata?.[field.key];

      if (typeof fieldValue === 'string') {
        eventMetadataValues[field.key] = fieldValue;
      }
    });
  }

  const { metadataJson: initialMetadataJson } = knownMetadata;
  let metadataJson = initialMetadataJson;

  if (eventDefinition?.metadataFields?.length && rule.metadata) {
    const strippedMetadata: Record<string, unknown> = {};

    Object.entries(rule.metadata).forEach(([key, value]) => {
      const isCoreField =
        key === 'category' || key === 'scenario' || key === 'channel';
      const isEventField = eventDefinition.metadataFields?.some(
        (field) => field.key === key,
      );

      if (!isCoreField && !isEventField) {
        strippedMetadata[key] = value;
      }
    });

    metadataJson = Object.keys(strippedMetadata).length
      ? JSON.stringify(strippedMetadata, null, 2)
      : '';
  }

  return {
    ruleName: rule.rule_name,
    sourceModule: rule.source_module,
    eventType: eventDefinition ? rule.event_type : CUSTOM_EVENT_TYPE_VALUE,
    customEventType: eventDefinition ? '' : rule.event_type,
    referenceType: rule.reference_type
      ? getPostingRuleReferenceDefinition(
          rule.source_module,
          rule.reference_type,
        )
        ? rule.reference_type
        : CUSTOM_REFERENCE_TYPE_VALUE
      : '',
    customReferenceType:
      rule.reference_type &&
      !getPostingRuleReferenceDefinition(
        rule.source_module,
        rule.reference_type,
      )
        ? rule.reference_type
        : '',
    direction: rule.direction,
    accountId: rule.account_id,
    taxRateId: rule.tax_rate_id ?? '',
    paymentMethodId: rule.payment_method_id
      ? String(rule.payment_method_id)
      : '',
    priority: String(rule.priority),
    status: rule.status,
    metadataCategory: knownMetadata.metadataCategory,
    metadataScenario: knownMetadata.metadataScenario,
    metadataChannel: knownMetadata.metadataChannel,
    eventMetadataValues,
    metadataJson,
  };
};

export const resolvePostingRuleEventType = (
  form: PostingRuleFormState,
): string =>
  form.eventType === CUSTOM_EVENT_TYPE_VALUE
    ? form.customEventType.trim()
    : form.eventType.trim();

export const serializePostingRuleMetadata = (
  form: PostingRuleFormState,
): Record<string, unknown> | null => {
  const metadata: Record<string, unknown> = {};

  if (form.metadataCategory.trim()) {
    metadata.category = form.metadataCategory.trim();
  }

  if (form.metadataScenario.trim()) {
    metadata.scenario = form.metadataScenario.trim();
  }

  if (form.metadataChannel.trim()) {
    metadata.channel = form.metadataChannel.trim();
  }

  Object.entries(form.eventMetadataValues).forEach(([key, value]) => {
    if (value.trim()) {
      metadata[key] = value.trim();
    }
  });

  if (form.metadataJson.trim()) {
    const parsedJson = JSON.parse(form.metadataJson) as Record<string, unknown>;
    Object.assign(metadata, parsedJson);
  }

  return Object.keys(metadata).length ? metadata : null;
};

export const createPostingRuleFormFromEventDefinition = (
  eventDefinition: PostingRuleEventDefinition,
): PostingRuleFormState => ({
  ruleName: eventDefinition.label,
  sourceModule: eventDefinition.sourceModule,
  eventType: eventDefinition.value,
  customEventType: '',
  referenceType: eventDefinition.defaultReferenceType ?? '',
  customReferenceType: '',
  direction: 'debit',
  accountId: '',
  taxRateId: '',
  paymentMethodId: '',
  priority: '100',
  status: 'active',
  metadataCategory: eventDefinition.defaultMetadata?.category ?? '',
  metadataScenario: eventDefinition.defaultMetadata?.scenario ?? '',
  metadataChannel: eventDefinition.defaultMetadata?.channel ?? '',
  eventMetadataValues: {},
  metadataJson: '',
});

const CORE_METADATA_LABELS: Record<string, string> = {
  category: 'Category',
  scenario: 'Scenario',
  channel: 'Channel',
};

const HIGHLIGHT_METADATA_KEYS = new Set([
  'refund_scope',
  'adjustment_scope',
  'nc_reason',
  'receivable_type',
  'payout_mode',
  'commission_stage',
  'recognition_basis',
  'deposit_kind',
  'cost_basis',
  'fee_policy',
  'sale_stage',
  'purchase_stage',
  'adjustment_reason',
]);

const formatMetadataEntry = (
  sourceModule: JournalSourceModule,
  eventType: string,
  key: string,
  value: unknown,
) => {
  const eventDefinition = getPostingRuleEventDefinition(
    sourceModule,
    eventType,
  );
  const eventField = eventDefinition?.metadataFields?.find(
    (field) => field.key === key,
  );
  const optionLabel =
    eventField?.type === 'select'
      ? eventField.options?.find((option) => option.value === String(value))
          ?.label
      : null;
  const label =
    eventField?.label || CORE_METADATA_LABELS[key] || key.replaceAll('_', ' ');

  return {
    key,
    label,
    value: optionLabel || String(value),
  };
};

export const getPostingRuleMetadataHighlights = (
  sourceModule: JournalSourceModule,
  eventType: string,
  metadata: Record<string, unknown> | null,
): { key: string; label: string; value: string }[] => {
  if (!metadata) {
    return [];
  }

  return Object.entries(metadata)
    .filter(
      ([key, value]) =>
        HIGHLIGHT_METADATA_KEYS.has(key) &&
        value !== null &&
        value !== undefined &&
        value !== '',
    )
    .map(([key, value]) =>
      formatMetadataEntry(sourceModule, eventType, key, value),
    );
};

export const formatPostingRuleMetadataSummary = (
  sourceModule: JournalSourceModule,
  eventType: string,
  metadata: Record<string, unknown> | null,
): string[] => {
  if (!metadata) {
    return [];
  }

  return Object.entries(metadata)
    .filter(
      ([key, value]) =>
        !HIGHLIGHT_METADATA_KEYS.has(key) &&
        value !== null &&
        value !== undefined &&
        value !== '',
    )
    .map(([key, value]) =>
      formatMetadataEntry(sourceModule, eventType, key, value),
    )
    .map((entry) => `${entry.label}: ${entry.value}`);
};

export const resolvePostingRuleReferenceType = (
  form: PostingRuleFormState,
): string | null => {
  if (!form.referenceType.trim()) {
    return null;
  }

  return form.referenceType === CUSTOM_REFERENCE_TYPE_VALUE
    ? form.customReferenceType.trim() || null
    : form.referenceType.trim();
};
